import { Box, Button, Card, CloseButton, Flex, FormControl, FormErrorMessage, FormLabel, Input, Select, Textarea, Tooltip } from "@chakra-ui/react";
import { CiTrash } from "react-icons/ci";
import { MdLibraryAdd } from "react-icons/md";
import { forwardRef, useState, useEffect, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Transaction } from "../../../api/transactions/transactionApi";
import { Entity } from "../../../api/entities/entityApi";
import { useUpdateTransaction, useUploadBenchmarkDiagram, useCreateTransactionWithBenchmarkAnalysisAsync } from "../../../api/transactions/transactionQueries";
import { FileContainer, FileUploader, MultiSelectControlled } from "am-tax-fe-core";
import { ALLOWED_BENCHMARK_EXTENSIONS } from "../../../utils/constants";
import {
    Category,
    TransactionType,
    Method,
    getCategoryDisplayName,
    getTransactionTypeDisplayName,
    getMethodDisplayName,
    CATEGORY_OPTIONS,
    TRANSACTION_TYPE_OPTIONS,
    METHOD_OPTIONS,
} from "../../../utils/transactionDisplayNames";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../api/queryKeys";

interface TransactionFormProps {
    entities?: Entity[];
    rowData?: Transaction;
    transactionNumber: number;
    clientId?: string;
    onClose: () => void;
    onValidationChange?: (hasErrors: boolean) => void;
    onSubmitSuccess?: () => void;
}

type OtherField = {
    Name: string;
    Text: string;
};

type FormFields = {
    name: string;
    category: string;
    transactionType: string;
    serviceProviders: string[];
    serviceRecipients: string[];
    method: string;
    // testedParties: string[];
    benchmarkAnalysis: string;
    industryAnalysis: FileList;
    otherTransactionType?: string; // For "Other" transaction type input
    year: number;
    researchAndDevelopment: string;
    purchasing: string;
    manufacturingAndAssembly: string;
    qualityAssurance: string;
    marketingAndSales: string;
    distributionAndLogistics: string;
    afterSalesServices: string;
    managementAndAdministration: string;
    otherFunction: string;
    manufacturingAndProduction: string;
    inventory: string;
    productLiabilityAndWarranty: string;
    market: string;
    foreignExchange: string;
    creditAndCollection: string;
    intangiblePropertyInfringement: string;
    otherRisk: string;
    assetsTangible: string;
    assetsTrademarksAndTradenames: string;
    assetsTechnicalKnowHow: string;
    assetsPatents: string;
    assetsOther: string;
};

export const TransactionForm = forwardRef<HTMLFormElement, TransactionFormProps>(
    ({ rowData, transactionNumber, entities, clientId, onClose, onValidationChange, onSubmitSuccess }, ref) => {
        const { mutateAsync: createTransactionWithBenchmarkAnalysisMutation } = useCreateTransactionWithBenchmarkAnalysisAsync();
        const [selectedSectionIndex, setSelectedSectionIndex] = useState<number>(0);
        const [submitted, setSubmitted] = useState(false);
        const [selectedBenchmarkFile, setSelectedBenchmarkFile] = useState<FileContainer | undefined>();
        const { mutateAsync: uploadBenchmarkFileMutation } = useUploadBenchmarkDiagram();
        const [assetsOtherFields, setAssetsOtherFields] = useState<OtherField[]>([]);
        const [risksOtherFields, setRisksOtherFields] = useState<OtherField[]>([]);
        const [functionsOtherFields, setFunctionsOtherFields] = useState<OtherField[]>([]);
        const [assetsOtherError, setAssetsOtherError] = useState<boolean>(false);
        const [risksOtherError, setRisksOtherError] = useState<boolean>(false);
        const [functionsOtherError, setFunctionsOtherError] = useState<boolean>(false);
        const { mutateAsync: updateTransactionMutation } = useUpdateTransaction();
        const [hasBenchmarkDiagram, setHasBenchmarkDiagram] = useState<boolean>(
            !!rowData?.benchmarkAnalysisBlobPath && rowData?.benchmarkAnalysisBlobPath.split("/").length > 1,
        );
        const currentYear = new Date().getFullYear();
        const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
        const debounceTimer = useRef<NodeJS.Timeout | null>(null);
        const queryClient = useQueryClient();

        // Debounce saving assetsOtherFields after 2 seconds of inactivity
        useEffect(() => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }

            debounceTimer.current = setTimeout(() => {
                // Save assetsOtherFields to the transaction if it exists
                if (rowData && assetsOtherFields.length > 0) {
                    // You can implement the save logic here
                    console.log("Saving assetsOtherFields:", assetsOtherFields);
                    // Example: updateTransactionMutation({ ...rowData, assetsOtherFields });
                }
            }, 2000);

            return () => {
                if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                }
            };
        }, [assetsOtherFields, rowData]);

        // Create form values dynamically to ensure they update with props
        const formValues: FormFields = {
            name: rowData?.name ?? `Transaction ${transactionNumber}`,
            category: rowData?.category ?? "",
            transactionType: rowData?.type ?? "",
            serviceProviders: rowData?.serviceProviders ?? [],
            serviceRecipients: rowData?.serviceRecipients ?? [],
            method: rowData?.method ?? "",
            // testedParties: rowData?.testedParties ?? [],
            benchmarkAnalysis: rowData?.benchmarkAnalysisBlobPath ?? "",
            industryAnalysis: undefined as unknown as FileList,
            otherTransactionType: rowData?.otherTransactionType ?? "",
            year: rowData?.year ?? currentYear,
            researchAndDevelopment: rowData?.researchAndDevelopment ?? "",
            purchasing: rowData?.purchasing ?? "",
            manufacturingAndAssembly: rowData?.manufacturingAndAssembly ?? "",
            qualityAssurance: rowData?.qualityAssurance ?? "",
            marketingAndSales: rowData?.marketingAndSales ?? "",
            distributionAndLogistics: rowData?.distributionAndLogistics ?? "",
            afterSalesServices: rowData?.afterSalesServices ?? "",
            managementAndAdministration: rowData?.managementAndAdministration ?? "",
            otherFunction: rowData?.otherFunction ?? "",
            manufacturingAndProduction: rowData?.manufacturingAndProduction ?? "",
            inventory: rowData?.inventory ?? "",
            productLiabilityAndWarranty: rowData?.productLiabilityAndWarranty ?? "",
            market: rowData?.market ?? "",
            foreignExchange: rowData?.foreignExchange ?? "",
            creditAndCollection: rowData?.creditAndCollection ?? "",
            intangiblePropertyInfringement: rowData?.intangiblePropertyInfringement ?? "",
            otherRisk: rowData?.otherRisk ?? "",
            assetsTangible: rowData?.assetsTangible ?? "",
            assetsTrademarksAndTradenames: rowData?.assetsTrademarksAndTradenames ?? "",
            assetsTechnicalKnowHow: rowData?.assetsTechnicalKnowHow ?? "",
            assetsPatents: rowData?.assetsPatents ?? "",
            assetsOther: rowData?.assetsOther ?? "",
        };

        const {
            register,
            handleSubmit,
            formState: { errors },
            watch,
            clearErrors,
            setError,
            setValue,
            reset,
            control,
        } = useForm<FormFields>({ values: formValues });

        // Reset form when transactionNumber changes (for new transaction forms)
        useEffect(() => {
            if (!rowData) {
                setSubmitted(false);
                setSelectedBenchmarkFile(undefined);
                setHasBenchmarkDiagram(false);
                reset({
                    name: `Transaction ${transactionNumber}`,
                    category: "",
                    transactionType: "",
                    serviceProviders: [],
                    serviceRecipients: [],
                    method: "",
                    // testedParties: [],
                    benchmarkAnalysis: "",
                    industryAnalysis: undefined as unknown as FileList,
                    otherTransactionType: "",
                    year: currentYear,
                    researchAndDevelopment: "",
                    purchasing: "",
                    manufacturingAndAssembly: "",
                    qualityAssurance: "",
                    marketingAndSales: "",
                    distributionAndLogistics: "",
                    afterSalesServices: "",
                    managementAndAdministration: "",
                    otherFunction: "",
                    manufacturingAndProduction: "",
                    inventory: "",
                    productLiabilityAndWarranty: "",
                    market: "",
                    foreignExchange: "",
                    creditAndCollection: "",
                    intangiblePropertyInfringement: "",
                    otherRisk: "",
                    assetsTangible: "",
                    assetsTrademarksAndTradenames: "",
                    assetsTechnicalKnowHow: "",
                    assetsPatents: "",
                    assetsOther: "",
                });
            } else {
                // Safely parse JSON fields with fallback to empty array
                let assetsOther = [];
                let risksOther = [];
                let functionsOther = [];
                try {
                    assetsOther = rowData?.assetsOther ? JSON.parse(rowData.assetsOther) : [];
                } catch {
                    assetsOther = [];
                }
                try {
                    risksOther = rowData?.otherRisk ? JSON.parse(rowData.otherRisk) : [];
                } catch {
                    risksOther = [];
                }
                try {
                    functionsOther = rowData?.otherFunction ? JSON.parse(rowData.otherFunction) : [];
                } catch {
                    functionsOther = [];
                }
                setAssetsOtherFields(assetsOther);
                setRisksOtherFields(risksOther);
                setFunctionsOtherFields(functionsOther);
            }
        }, [transactionNumber, rowData, reset, currentYear]);

        // Watch for form validation changes and notify parent
        useEffect(() => {
            const hasErrors = Object.keys(errors).length > 0;
            onValidationChange?.(hasErrors);
        }, [errors, onValidationChange]);

        function hasOtherFieldErrors(fields: OtherField[]): boolean {
            return fields.some(field => !field.Name.trim() || !field.Text.trim());
        }

        const onSubmit: SubmitHandler<FormFields> = async data => {
            if (!submitted || rowData) {
                // Check if benchmark file is required and validate
                if (!selectedBenchmarkFile && !hasBenchmarkDiagram && !data.benchmarkAnalysis) {
                    setError("benchmarkAnalysis", {
                        type: "manual",
                        message: "Benchmark analysis file is required",
                    });
                    return;
                }

                const assetsOtherErrorVar = hasOtherFieldErrors(assetsOtherFields);
                const risksOtherErrorVar = hasOtherFieldErrors(risksOtherFields);
                const functionsOtherErrorVar = hasOtherFieldErrors(functionsOtherFields);

                setAssetsOtherError(assetsOtherErrorVar);
                setRisksOtherError(risksOtherErrorVar);
                setFunctionsOtherError(functionsOtherErrorVar);

                if (assetsOtherErrorVar || risksOtherErrorVar || functionsOtherErrorVar) {
                    return;
                }

                const transactionId = rowData?.id;
                if (!rowData) {
                    if (selectedBenchmarkFile) {
                        await createTransactionWithBenchmarkAnalysisMutation({
                            file: selectedBenchmarkFile,
                            transaction: {
                                name: data.name,
                                clientId: clientId || "",
                                category: data.category as Category,
                                type: data.transactionType as TransactionType,
                                otherTransactionType: data.otherTransactionType || "",
                                serviceProviders: data.serviceProviders,
                                serviceRecipients: data.serviceRecipients,
                                method: data.method as Method,
                                testedParties: ["PLACEHOLDER"],
                                benchmarkAnalysisBlobPath: data.benchmarkAnalysis || "/",
                                year: data.year || currentYear,
                                researchAndDevelopment: data.researchAndDevelopment,
                                purchasing: data.purchasing,
                                manufacturingAndAssembly: data.manufacturingAndAssembly,
                                qualityAssurance: data.qualityAssurance,
                                marketingAndSales: data.marketingAndSales,
                                distributionAndLogistics: data.distributionAndLogistics,
                                afterSalesServices: data.afterSalesServices,
                                managementAndAdministration: data.managementAndAdministration,
                                otherFunction: JSON.stringify(functionsOtherFields ?? []),
                                manufacturingAndProduction: data.manufacturingAndProduction,
                                inventory: data.inventory,
                                productLiabilityAndWarranty: data.productLiabilityAndWarranty,
                                market: data.market,
                                foreignExchange: data.foreignExchange,
                                creditAndCollection: data.creditAndCollection,
                                intangiblePropertyInfringement: data.intangiblePropertyInfringement,
                                otherRisk: JSON.stringify(risksOtherFields ?? []),
                                assetsTangible: data.assetsTangible,
                                assetsTrademarksAndTradenames: data.assetsTrademarksAndTradenames,
                                assetsTechnicalKnowHow: data.assetsTechnicalKnowHow,
                                assetsPatents: data.assetsPatents,
                                assetsOther: JSON.stringify(assetsOtherFields ?? []),
                            },
                        });
                        setSubmitted(true);
                        onSubmitSuccess?.();
                    }
                } else {
                    await updateTransactionMutation({
                        file: selectedBenchmarkFile || undefined,
                        transaction: {
                            id: rowData.id,
                            clientId: clientId || "",
                            name: data.name,
                            category: data.category as Category,
                            type: data.transactionType as TransactionType,
                            otherTransactionType: data.otherTransactionType || "",
                            serviceProviders: data.serviceProviders,
                            serviceRecipients: data.serviceRecipients,
                            method: data.method as Method,
                            testedParties: ["PLACEHOLDER"],
                            benchmarkAnalysisBlobPath: data.benchmarkAnalysis || "/",
                            year: data.year,
                            researchAndDevelopment: data.researchAndDevelopment,
                            purchasing: data.purchasing,
                            manufacturingAndAssembly: data.manufacturingAndAssembly,
                            qualityAssurance: data.qualityAssurance,
                            marketingAndSales: data.marketingAndSales,
                            distributionAndLogistics: data.distributionAndLogistics,
                            afterSalesServices: data.afterSalesServices,
                            managementAndAdministration: data.managementAndAdministration,
                            otherFunction: JSON.stringify(functionsOtherFields ?? []),
                            manufacturingAndProduction: data.manufacturingAndProduction,
                            inventory: data.inventory,
                            productLiabilityAndWarranty: data.productLiabilityAndWarranty,
                            market: data.market,
                            foreignExchange: data.foreignExchange,
                            creditAndCollection: data.creditAndCollection,
                            intangiblePropertyInfringement: data.intangiblePropertyInfringement,
                            otherRisk: JSON.stringify(risksOtherFields ?? []),
                            assetsTangible: data.assetsTangible,
                            assetsTrademarksAndTradenames: data.assetsTrademarksAndTradenames,
                            assetsTechnicalKnowHow: data.assetsTechnicalKnowHow,
                            assetsPatents: data.assetsPatents,
                            assetsOther: JSON.stringify(assetsOtherFields ?? []),
                        },
                    });
                    onSubmitSuccess?.();
                    onClose();
                }

                await queryClient.invalidateQueries(queryKeys.transactions.all);

                // Upload benchmark file if one was selected
                if (selectedBenchmarkFile && transactionId) {
                    await uploadBenchmarkFileMutation({
                        file: selectedBenchmarkFile,
                        transactionId: transactionId,
                    });
                }
            }
        };

        const onFileAdded = async (fileContainer: FileContainer) => {
            const fileName = fileContainer.file.name;
            const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

            if (ALLOWED_BENCHMARK_EXTENSIONS.includes(fileExtension)) {
                setSelectedBenchmarkFile(fileContainer);
                setValue("benchmarkAnalysis", fileContainer.file.name);
                clearErrors("benchmarkAnalysis");
            } else {
                setSelectedBenchmarkFile(undefined);
                setValue("benchmarkAnalysis", "");
                setError("benchmarkAnalysis", {
                    type: "manual",
                    message: "Invalid file format.",
                });
            }
        };

        const handleRemoveBenchmarkDiagram = () => {
            setSelectedBenchmarkFile(undefined);
            setHasBenchmarkDiagram(false);
            setValue("benchmarkAnalysis", "");
            // This will trigger validation since the field is required
            setError("benchmarkAnalysis", {
                type: "manual",
                message: "Benchmark analysis file is required",
            });
        };

        const getRecipientLabel = () => {
            const type = watch("transactionType");

            if (type === "Distribution") {
                return "Receivers";
            }

            if (type === "Procurement") {
                return "Licensees";
            }

            return "Service Recipients";
        };

        const getProviderLabel = () => {
            const type = watch("transactionType");

            if (type === "Distribution") {
                return "Distributors";
            }

            if (type === "Procurement") {
                return "Licensors";
            }

            return "Service Providers / Tested Parties";
        };

        return (
            <>
                {submitted ? (
                    <Flex align="center" bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200" p={4} mb={4} boxShadow="sm">
                        <Flex align="center" mr={3}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="12" fill="#38A169" />
                                <path d="M17 9.5L11 15.5L7 11.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Flex>
                        <div style={{ color: "#276749", fontWeight: 500 }}>Transaction {transactionNumber} submitted successfully!</div>
                    </Flex>
                ) : (
                    <>
                        <Flex mt="1.5rem" mb="1.5rem" gap="0.5rem">
                            <Button variant={selectedSectionIndex === 0 ? "solid" : "outline"} onClick={() => setSelectedSectionIndex(0)}>
                                Main Fields
                            </Button>
                            <Button variant={selectedSectionIndex === 1 ? "solid" : "outline"} onClick={() => setSelectedSectionIndex(1)}>
                                Functions
                            </Button>
                            <Button variant={selectedSectionIndex === 2 ? "solid" : "outline"} onClick={() => setSelectedSectionIndex(2)}>
                                Risks
                            </Button>
                            <Button variant={selectedSectionIndex === 3 ? "solid" : "outline"} onClick={() => setSelectedSectionIndex(3)}>
                                Assets
                            </Button>
                        </Flex>
                        <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
                            {selectedSectionIndex === 0 && (
                                <Flex flexDirection="column" gap={6} px="1rem" pt="1rem">
                                    <Box>
                                        <Flex gap="1rem">
                                            <FormControl isRequired isInvalid={!!errors?.name} mb={4}>
                                                <FormLabel>Name</FormLabel>
                                                <Input {...register("name", { required: "Name is required" })} placeholder="Transaction name" />
                                                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                                            </FormControl>
                                            <FormControl isInvalid={!!errors.year}>
                                                <FormLabel>Year</FormLabel>
                                                <Select
                                                    {...register("year", {
                                                        required: "Year is required",
                                                        valueAsNumber: true,
                                                    })}
                                                    placeholder="Select year"
                                                >
                                                    {yearOptions.map(year => (
                                                        <option key={year} value={year}>
                                                            {year}
                                                        </option>
                                                    ))}
                                                </Select>
                                                <FormErrorMessage>{errors.year?.message}</FormErrorMessage>
                                            </FormControl>
                                        </Flex>

                                        <FormControl isRequired isInvalid={!!errors?.category} mb={4}>
                                            <FormLabel>Category</FormLabel>
                                            <Select {...register("category", { required: "Category is required" })} placeholder="Select category">
                                                {CATEGORY_OPTIONS.map(opt => (
                                                    <option key={opt} value={opt}>
                                                        {getCategoryDisplayName(opt)}
                                                    </option>
                                                ))}
                                            </Select>
                                            <FormErrorMessage>{errors.category?.message}</FormErrorMessage>
                                        </FormControl>

                                        <FormControl isRequired isInvalid={!!errors?.transactionType} mb={4}>
                                            <FormLabel>Transaction type</FormLabel>
                                            <Select
                                                {...register("transactionType", { required: "Transaction type is required" })}
                                                placeholder="Select transaction type"
                                                disabled={!watch("category")}
                                            >
                                                {(TRANSACTION_TYPE_OPTIONS[watch("category") as Category] || []).map(opt => (
                                                    <option key={opt} value={opt}>
                                                        {getTransactionTypeDisplayName(opt)}
                                                    </option>
                                                ))}
                                            </Select>
                                            <FormErrorMessage>{errors.transactionType?.message}</FormErrorMessage>
                                        </FormControl>

                                        {watch("transactionType") == "Other" && (
                                            <FormControl isRequired mb={4}>
                                                <FormLabel>Custom Transaction Type</FormLabel>
                                                <Input {...register("otherTransactionType")} placeholder="Enter transaction type" />
                                            </FormControl>
                                        )}
                                    </Box>

                                    {/* Entities Section */}
                                    <Box>
                                        <FormControl isRequired isInvalid={!!errors?.serviceRecipients} mb={4}>
                                            <FormLabel>{getRecipientLabel()}</FormLabel>
                                            <MultiSelectControlled
                                                control={control}
                                                options={(Array.isArray(entities) ? entities : []).map(entity => ({
                                                    value: entity.id,
                                                    label: `${entity.abbreviatedName}`,
                                                }))}
                                                name={"serviceRecipients"}
                                                rules={{ required: "At least one service recipient is required" }}
                                            />
                                            <FormErrorMessage>{errors.serviceRecipients?.message}</FormErrorMessage>
                                        </FormControl>

                                        <FormControl isRequired isInvalid={!!errors?.serviceProviders} mb={4}>
                                            <FormLabel>{getProviderLabel()}</FormLabel>
                                            <MultiSelectControlled
                                                control={control}
                                                options={(Array.isArray(entities) ? entities : []).map(entity => ({
                                                    value: entity.id,
                                                    label: `${entity.abbreviatedName}`,
                                                }))}
                                                name={"serviceProviders"}
                                                rules={{ required: "At least one service provider is required" }}
                                            />
                                            <FormErrorMessage>{errors.serviceProviders?.message}</FormErrorMessage>
                                        </FormControl>
                                    </Box>

                                    {/* Analysis Section */}
                                    <Box>
                                        <FormControl isRequired isInvalid={!!errors?.method} mb={4}>
                                            <FormLabel>Method</FormLabel>
                                            <Select
                                                {...register("method", { required: "Method is required" })}
                                                placeholder="Select method"
                                                disabled={!watch("category")}
                                            >
                                                {(METHOD_OPTIONS[watch("category") as Category] || [])
                                                    .sort((a, b) => getMethodDisplayName(a).localeCompare(getMethodDisplayName(b)))
                                                    .map(opt => (
                                                        <option key={opt} value={opt}>
                                                            {getMethodDisplayName(opt)}
                                                        </option>
                                                    ))}
                                            </Select>
                                            <FormErrorMessage>{errors.method?.message}</FormErrorMessage>
                                        </FormControl>

                                        {/*
                                        <FormControl isRequired isInvalid={!!errors.testedParties}>
                                            <FormLabel>Tested Parties</FormLabel>
                                            <MultiSelectControlled
                                                control={control}
                                                options={(Array.isArray(entities) ? entities : []).map(entity => ({
                                                    value: entity.id,
                                                    label: `${entity.abbreviatedName}`,
                                                }))}
                                                name={"testedParties"}
                                                rules={{ required: "At least one tested party is required" }}
                                            />
                                            <FormHelperText>Select one or more underlying entities</FormHelperText>
                                            <FormErrorMessage>{errors.testedParties?.message as string}</FormErrorMessage>
                                        </FormControl>
                                        */}

                                        {hasBenchmarkDiagram ? (
                                            <Box>
                                                <FormLabel>Benchmark Analysis:</FormLabel>
                                                <Flex align="center" mt="1rem" mb="1rem">
                                                    <p>{rowData?.benchmarkAnalysisBlobPath?.split("/").pop()}</p>
                                                    <Tooltip label="Remove benchmark diagram">
                                                        <CloseButton ml="1rem" variant="ghost" aria-label="Close" onClick={handleRemoveBenchmarkDiagram} />
                                                    </Tooltip>
                                                </Flex>
                                                <FormErrorMessage>{errors.benchmarkAnalysis?.message}</FormErrorMessage>
                                            </Box>
                                        ) : (
                                            <FormControl isRequired isInvalid={!!errors?.benchmarkAnalysis}>
                                                <FormLabel>Benchmark Analysis:</FormLabel>
                                                <FileUploader
                                                    {...register("benchmarkAnalysis")}
                                                    maxSize={10485760}
                                                    maxFiles={1}
                                                    onFileAdded={onFileAdded}
                                                    accept={{
                                                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
                                                    }}
                                                />
                                                <FormErrorMessage>{errors.benchmarkAnalysis?.message}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Box>
                                </Flex>
                            )}

                            {selectedSectionIndex === 1 && (
                                <Flex flexDirection="column" gap={6} px="1rem" pt="1rem">
                                    <FormControl isInvalid={!!errors?.researchAndDevelopment}>
                                        <FormLabel>Research and Development:</FormLabel>
                                        <Textarea {...register("researchAndDevelopment")} />
                                        <FormErrorMessage>{errors.researchAndDevelopment?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.purchasing}>
                                        <FormLabel>Purchasing:</FormLabel>
                                        <Textarea {...register("purchasing")} />
                                        <FormErrorMessage>{errors.purchasing?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.manufacturingAndAssembly}>
                                        <FormLabel>Manufacturing &amp; Assembly:</FormLabel>
                                        <Textarea {...register("manufacturingAndAssembly")} />
                                        <FormErrorMessage>{errors.manufacturingAndAssembly?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.qualityAssurance}>
                                        <FormLabel>Quality Assurance:</FormLabel>
                                        <Textarea {...register("qualityAssurance")} />
                                        <FormErrorMessage>{errors.qualityAssurance?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.marketingAndSales}>
                                        <FormLabel>Marketing &amp; Sales:</FormLabel>
                                        <Textarea {...register("marketingAndSales")} />
                                        <FormErrorMessage>{errors.marketingAndSales?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.distributionAndLogistics}>
                                        <FormLabel>Distribution &amp; Logistics:</FormLabel>
                                        <Textarea {...register("distributionAndLogistics")} />
                                        <FormErrorMessage>{errors.distributionAndLogistics?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.afterSalesServices}>
                                        <FormLabel>After-Sales Services:</FormLabel>
                                        <Textarea {...register("afterSalesServices")} />
                                        <FormErrorMessage>{errors.afterSalesServices?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.managementAndAdministration}>
                                        <FormLabel>Management and Administration:</FormLabel>
                                        <Textarea {...register("managementAndAdministration")} />
                                        <FormErrorMessage>{errors.managementAndAdministration?.message}</FormErrorMessage>
                                    </FormControl>

                                    {functionsOtherFields.length > 0 && (
                                        <>
                                            {functionsOtherFields.map((field, index) => (
                                                <Card
                                                    key={index}
                                                    p="1rem"
                                                    borderColor={functionsOtherError && (!field.Name.trim() || !field.Text.trim()) ? "red.500" : undefined}
                                                    borderWidth={functionsOtherError && (!field.Name.trim() || !field.Text.trim()) ? "2px" : undefined}
                                                >
                                                    <Flex justify="flex-end" mb="0.5rem">
                                                        <Input
                                                            placeholder="Field Name"
                                                            value={field.Name}
                                                            onChange={e => {
                                                                const newFields = [...functionsOtherFields];
                                                                newFields[index].Name = e.target.value;
                                                                setFunctionsOtherFields(newFields);
                                                            }}
                                                            mb="0.5rem"
                                                            mr="1rem"
                                                        />
                                                        <Tooltip label="Remove field">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                colorScheme="red"
                                                                onClick={() => {
                                                                    const newFields = functionsOtherFields.filter((_, i) => i !== index);
                                                                    setFunctionsOtherFields(newFields);
                                                                }}
                                                                leftIcon={<CiTrash />}
                                                                aria-label="Remove field"
                                                            >
                                                                Remove
                                                            </Button>
                                                        </Tooltip>
                                                    </Flex>
                                                    <FormControl>
                                                        <Textarea
                                                            value={field.Text}
                                                            onChange={e => {
                                                                const newFields = [...functionsOtherFields];
                                                                newFields[index].Text = e.target.value;
                                                                setFunctionsOtherFields(newFields);
                                                            }}
                                                            placeholder="Enter field description..."
                                                        />
                                                    </FormControl>
                                                </Card>
                                            ))}
                                        </>
                                    )}

                                    <Box display="flex" justifyContent="end">
                                        <Button
                                            leftIcon={<MdLibraryAdd />}
                                            variant="outline"
                                            colorScheme="blue"
                                            onClick={() => setFunctionsOtherFields([...functionsOtherFields, { Name: "", Text: "" }])}
                                            size="sm"
                                        >
                                            Add Other Category
                                        </Button>
                                    </Box>
                                </Flex>
                            )}

                            {selectedSectionIndex === 2 && (
                                <Flex flexDirection="column" gap={6} px="1rem" pt="1rem">
                                    <FormControl isInvalid={!!errors?.manufacturingAndProduction}>
                                        <FormLabel>Manufacturing and Production:</FormLabel>
                                        <Textarea {...register("manufacturingAndProduction")} />
                                        <FormErrorMessage>{errors.manufacturingAndProduction?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.inventory}>
                                        <FormLabel>Inventory:</FormLabel>
                                        <Textarea {...register("inventory")} />
                                        <FormErrorMessage>{errors.inventory?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.productLiabilityAndWarranty}>
                                        <FormLabel>Product Liability and Warranty:</FormLabel>
                                        <Textarea {...register("productLiabilityAndWarranty")} />
                                        <FormErrorMessage>{errors.productLiabilityAndWarranty?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.market}>
                                        <FormLabel>Market:</FormLabel>
                                        <Textarea {...register("market")} />
                                        <FormErrorMessage>{errors.market?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.foreignExchange}>
                                        <FormLabel>Foreign Exchange:</FormLabel>
                                        <Textarea {...register("foreignExchange")} />
                                        <FormErrorMessage>{errors.foreignExchange?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.creditAndCollection}>
                                        <FormLabel>Credit and Collection:</FormLabel>
                                        <Textarea {...register("creditAndCollection")} />
                                        <FormErrorMessage>{errors.creditAndCollection?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.intangiblePropertyInfringement}>
                                        <FormLabel>Intangible Property Infringement:</FormLabel>
                                        <Textarea {...register("intangiblePropertyInfringement")} />
                                        <FormErrorMessage>{errors.intangiblePropertyInfringement?.message}</FormErrorMessage>
                                    </FormControl>

                                    {risksOtherFields.length > 0 && (
                                        <>
                                            {risksOtherFields.map((field, index) => (
                                                <Card
                                                    key={index}
                                                    p="1rem"
                                                    borderColor={risksOtherError && (!field.Name.trim() || !field.Text.trim()) ? "red.500" : undefined}
                                                    borderWidth={risksOtherError && (!field.Name.trim() || !field.Text.trim()) ? "2px" : undefined}
                                                >
                                                    <Flex justify="flex-end" mb="0.5rem">
                                                        <Input
                                                            placeholder="Field Name"
                                                            value={field.Name}
                                                            onChange={e => {
                                                                const newFields = [...risksOtherFields];
                                                                newFields[index].Name = e.target.value;
                                                                setRisksOtherFields(newFields);
                                                            }}
                                                            mb="0.5rem"
                                                            mr="1rem"
                                                        />
                                                        <Tooltip label="Remove field">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                colorScheme="red"
                                                                onClick={() => {
                                                                    const newFields = risksOtherFields.filter((_, i) => i !== index);
                                                                    setRisksOtherFields(newFields);
                                                                }}
                                                                leftIcon={<CiTrash />}
                                                                aria-label="Remove field"
                                                            >
                                                                Remove
                                                            </Button>
                                                        </Tooltip>
                                                    </Flex>
                                                    <FormControl>
                                                        <Textarea
                                                            value={field.Text}
                                                            onChange={e => {
                                                                const newFields = [...risksOtherFields];
                                                                newFields[index].Text = e.target.value;
                                                                setRisksOtherFields(newFields);
                                                            }}
                                                            placeholder="Enter field description..."
                                                        />
                                                    </FormControl>
                                                </Card>
                                            ))}
                                        </>
                                    )}

                                    <Box display="flex" justifyContent="end">
                                        <Button
                                            leftIcon={<MdLibraryAdd />}
                                            variant="outline"
                                            colorScheme="blue"
                                            onClick={() => setRisksOtherFields([...risksOtherFields, { Name: "", Text: "" }])}
                                            size="sm"
                                        >
                                            Add Other Category
                                        </Button>
                                    </Box>
                                </Flex>
                            )}

                            {selectedSectionIndex === 3 && (
                                <Flex flexDirection="column" gap={6} px="1rem" pt="1rem">
                                    <FormControl isInvalid={!!errors?.assetsTangible}>
                                        <FormLabel>Tangible Assets</FormLabel>
                                        <Textarea {...register("assetsTangible")} />
                                        <FormErrorMessage>{errors.assetsTangible?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.assetsTrademarksAndTradenames}>
                                        <FormLabel>Trademarks and Tradenames</FormLabel>
                                        <Textarea {...register("assetsTrademarksAndTradenames")} />
                                        <FormErrorMessage>{errors.assetsTrademarksAndTradenames?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.assetsTechnicalKnowHow}>
                                        <FormLabel>Technical Know-How</FormLabel>
                                        <Textarea {...register("assetsTechnicalKnowHow")} />
                                        <FormErrorMessage>{errors.assetsTechnicalKnowHow?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors?.assetsPatents}>
                                        <FormLabel>Patents</FormLabel>
                                        <Textarea {...register("assetsPatents")} />
                                        <FormErrorMessage>{errors.assetsPatents?.message}</FormErrorMessage>
                                    </FormControl>

                                    {assetsOtherFields.length > 0 && (
                                        <>
                                            {assetsOtherFields.map((field, index) => (
                                                <Card
                                                    key={index}
                                                    p="1rem"
                                                    borderColor={assetsOtherError && (!field.Name.trim() || !field.Text.trim()) ? "red.500" : undefined}
                                                    borderWidth={assetsOtherError && (!field.Name.trim() || !field.Text.trim()) ? "2px" : undefined}
                                                >
                                                    <Flex justify="flex-end" mb="0.5rem">
                                                        <Input
                                                            placeholder="Field Name"
                                                            value={field.Name}
                                                            onChange={e => {
                                                                const newFields = [...assetsOtherFields];
                                                                newFields[index].Name = e.target.value;
                                                                setAssetsOtherFields(newFields);
                                                            }}
                                                            mb="0.5rem"
                                                            mr="1rem"
                                                        />
                                                        <Tooltip label="Remove field">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                colorScheme="red"
                                                                onClick={() => {
                                                                    const newFields = assetsOtherFields.filter((_, i) => i !== index);
                                                                    setAssetsOtherFields(newFields);
                                                                }}
                                                                leftIcon={<CiTrash />}
                                                                aria-label="Remove field"
                                                            >
                                                                Remove
                                                            </Button>
                                                        </Tooltip>
                                                    </Flex>
                                                    <FormControl>
                                                        <Textarea
                                                            value={field.Text}
                                                            onChange={e => {
                                                                const newFields = [...assetsOtherFields];
                                                                newFields[index].Text = e.target.value;
                                                                setAssetsOtherFields(newFields);
                                                            }}
                                                            placeholder="Enter field description..."
                                                        />
                                                    </FormControl>
                                                </Card>
                                            ))}
                                        </>
                                    )}

                                    <Box display="flex" justifyContent="end">
                                        <Button
                                            leftIcon={<MdLibraryAdd />}
                                            variant="outline"
                                            colorScheme="blue"
                                            onClick={() => setAssetsOtherFields([...assetsOtherFields, { Name: "", Text: "" }])}
                                            size="sm"
                                        >
                                            Add Other Category
                                        </Button>
                                    </Box>
                                </Flex>
                            )}
                        </form>
                    </>
                )}
            </>
        );
    },
);
